/**
 * Settings Page — Profile, Organization, API Keys, Notifications
 *
 * Profile section is editable (toggleable).
 * API Keys section displays admin contact guidance.
 * Notifications section has toggle switches (visual/local state).
 */

import { useState, useCallback } from 'react';
import { User, Building, Key, Bell, Pencil, Save, X, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ROLE_LABELS } from '@/config/roles';

export function SettingsPage() {
  const { user, icRole } = useAuth();

  // ─── Profile Edit State ───────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handleEditToggle = useCallback(() => {
    if (editing) {
      // Cancel: reset to original values
      setFullName(user?.full_name ?? '');
      setEmail(user?.email ?? '');
    }
    setEditing((prev) => !prev);
    setSaveSuccess(false);
  }, [editing, user]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    setSaveSuccess(false);
    // Stub: no backend endpoint for profile update yet
    // Simulate a short delay for UX feedback
    await new Promise((resolve) => setTimeout(resolve, 600));
    setSaving(false);
    setSaveSuccess(true);
    setEditing(false);
    // In a real implementation, we would call:
    // await apiClient.put('/api/v1/users/me', { full_name: fullName, email });
  }, []);

  // ─── Notification Toggles (local state only) ─────────────────
  const [notifEmailReports, setNotifEmailReports] = useState(true);
  const [notifEmailCompliance, setNotifEmailCompliance] = useState(true);
  const [notifEmailSubmissions, setNotifEmailSubmissions] = useState(false);
  const [notifInAppActivity, setNotifInAppActivity] = useState(true);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-semibold text-ic-text-primary">Settings</h1>
        <p className="text-xs text-ic-text-tertiary">Manage your account and preferences</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Profile Card */}
        <div className="rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <User className="h-4 w-4 text-ic-text-tertiary" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">Profile</h3>
            </div>
            {!editing ? (
              <button
                onClick={handleEditToggle}
                className="flex items-center gap-1 rounded-md border border-ic-border px-2 py-1 text-[10px] font-medium text-ic-text-secondary hover:bg-ic-hover hover:text-ic-text-primary transition-colors"
              >
                <Pencil className="h-3 w-3" />
                Edit Profile
              </button>
            ) : (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={handleEditToggle}
                  className="flex items-center gap-1 rounded-md border border-ic-border px-2 py-1 text-[10px] font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
                >
                  <X className="h-3 w-3" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1 rounded-md bg-ic-accent px-2 py-1 text-[10px] font-medium text-white hover:bg-ic-accent-hover disabled:opacity-50 transition-colors"
                >
                  <Save className="h-3 w-3" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            )}
          </div>

          {saveSuccess && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
              <span className="text-[10px] text-emerald-400">Profile saved successfully.</span>
            </div>
          )}

          <div className="space-y-3">
            {editing ? (
              <>
                <EditableRow label="Name" value={fullName} onChange={setFullName} />
                <EditableRow label="Email" value={email} onChange={setEmail} type="email" />
                <InfoRow label="Role" value={ROLE_LABELS[icRole]} />
                <InfoRow label="Status" value={user?.is_active ? 'Active' : 'Inactive'} />
              </>
            ) : (
              <>
                <InfoRow label="Name" value={user?.full_name ?? '--'} />
                <InfoRow label="Email" value={user?.email ?? '--'} />
                <InfoRow label="Role" value={ROLE_LABELS[icRole]} />
                <InfoRow label="Status" value={user?.is_active ? 'Active' : 'Inactive'} />
              </>
            )}
          </div>
        </div>

        {/* Organization Card */}
        <div className="rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="flex items-center gap-3 mb-4">
            <Building className="h-4 w-4 text-ic-text-tertiary" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">Organization</h3>
          </div>
          <div className="space-y-3">
            <InfoRow label="Org ID" value={user?.organization_id?.toString() ?? '--'} />
            <InfoRow label="Tier" value={capitalize(user?.subscription_tier ?? 'free')} />
          </div>
        </div>

        {/* API Keys */}
        <div className="rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="flex items-center gap-3 mb-4">
            <Key className="h-4 w-4 text-ic-text-tertiary" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">API Keys</h3>
          </div>
          <p className="text-xs text-ic-text-secondary">
            Contact your organization administrator to manage API keys and integration credentials.
          </p>
          <div className="mt-3 rounded-md bg-ic-accent/5 border border-ic-accent/20 px-3 py-2">
            <p className="text-[10px] text-ic-text-tertiary">
              API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.
            </p>
          </div>
        </div>

        {/* Notifications */}
        <div className="rounded-lg border border-ic-border bg-ic-card p-4">
          <div className="flex items-center gap-3 mb-4">
            <Bell className="h-4 w-4 text-ic-text-tertiary" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">Notifications</h3>
          </div>
          <div className="space-y-3">
            <ToggleRow
              label="Report generation alerts"
              description="Email when a report is generated or fails"
              checked={notifEmailReports}
              onChange={setNotifEmailReports}
            />
            <ToggleRow
              label="Compliance deadline reminders"
              description="Email reminders for upcoming filing deadlines"
              checked={notifEmailCompliance}
              onChange={setNotifEmailCompliance}
            />
            <ToggleRow
              label="ESG submission updates"
              description="Email when investee submissions change status"
              checked={notifEmailSubmissions}
              onChange={setNotifEmailSubmissions}
            />
            <ToggleRow
              label="In-app activity feed"
              description="Show real-time activity notifications in header"
              checked={notifInAppActivity}
              onChange={setNotifInAppActivity}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-Components ────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-ic-text-tertiary">{label}</span>
      <span className="text-xs font-medium text-ic-text-primary">{value}</span>
    </div>
  );
}

function EditableRow({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  type?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-xs text-ic-text-tertiary shrink-0">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full max-w-[220px] rounded-md border border-ic-border bg-ic-input px-2 py-1 text-xs text-ic-text-primary focus:border-ic-accent focus:outline-none"
      />
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-medium text-ic-text-primary">{label}</p>
        <p className="text-[10px] text-ic-text-tertiary">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
          checked ? 'bg-ic-accent' : 'bg-slate-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
